import React, {
  Component,
  createContext,
  FunctionComponent,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import { trackEvent, captureException } from '@/analytics';
import {
  claimChunkRecovery,
  isChunkLoadError,
  recoverFromChunkError
} from '@/components/App/chunkPreloadError';

const ConfirmationModal = lazy(() =>
  import('@/components/ConfirmationModal/ConfirmationModal').then((module) => ({
    default: module.ConfirmationModal
  }))
);

type ConfirmationStatus = 'info' | 'warning' | 'danger';

interface ModalConfig {
  status?: ConfirmationStatus;
  title?: string | React.JSX.Element;
  content?: string | React.JSX.Element;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onClose?: () => void;
  /**
   * Si devuelve una promesa, el modal queda abierto con el botón de confirmar en
   * estado pendiente hasta que se resuelva (y recién ahí se cierra).
   */
  onConfirm?: () => unknown;
  /** Destino del portal del modal (por defecto `document.body`). */
  container?: HTMLElement | null;
}

type ConfirmationContextType = {
  confirm: (modalConfig: ModalConfig) => void;
};

const defaultConfirmOptions: ModalConfig = {
  status: 'info',
  title: 'Confirmar Cambios',
  content: '¿Quieres confirmar la acción?',
  message: '',
  confirmText: 'Si, guardar',
  cancelText: 'No, volver'
};

// El modal monta en GlobalProviders, ARRIBA del GlobalErrorBoundary de la app: si su chunk
// no baja (pestaña abierta de un deploy que Vercel ya no sirve), el import() resuelve
// `undefined` (App.tsx cancela 'vite:preloadError') y React tira «reading 'default'» sin
// ningún boundary que lo ataje → la app entera se desmonta y queda la pantalla en blanco.
// Pasaba en la Agenda al cancelar un turno o bloquear horarios. Este boundary hace lo mismo
// que el de la app con un chunk que no llegó: recarga en silencio. Si no es eso, cierra el
// modal y lo reporta, pero no voltea la pantalla de atrás.
class ConfirmationModalBoundary extends Component<
  { children: React.ReactNode; onError: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    // Misma escalera que el boundary de la app (reload → modo seguro); en vez de la
    // pantalla terminal, cerramos el modal y lo reportamos.
    const recovery = isChunkLoadError(error) ? claimChunkRecovery() : null;
    if (recovery && recovery.step !== 'screen') {
      const step = recovery.step;
      try {
        trackEvent('chunk_recovery', {
          screen: 'confirmation_modal',
          outcome: step === 'reload' ? 'auto_reload' : 'safe_reload',
          attempt: recovery.attempt,
          route: window.location.pathname,
          error_name: error?.name || 'Error'
        });
      } catch {
        /* el tracking nunca bloquea la recuperación */
      }
      setTimeout(() => void recoverFromChunkError(step), 150);
      return;
    }
    try {
      captureException(error, { source: 'confirmation_modal' });
    } catch {
      /* error tracking must never block recovery */
    }
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const ConfirmationContext = createContext<ConfirmationContextType>({
  confirm: (modalConfig: ModalConfig) => modalConfig
});

interface ConfirmationProviderPropsI {
  children?: React.ReactNode;
}

export const ConfirmationProvider: FunctionComponent<ConfirmationProviderPropsI> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // El modal queda montado después del primer confirm(): Radix lo mantiene vivo
  // mientras corre la animación de salida y lo saca del DOM al terminar. Antes se
  // desmontaba en el mismo render del cierre y desaparecía de golpe. Hasta el
  // primer confirm() no se monta, así el chunk del modal sigue siendo diferido.
  const [isModalMounted, setIsModalMounted] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState<ModalConfig>(defaultConfirmOptions);

  const handleModalClose = useCallback((isOpen: boolean) => {
    setIsModalOpen(isOpen);

    // Don't reset options on close — Radix keeps the dialog mounted during the
    // dismiss animation, and a synchronous reset would flash the default
    // "Confirmar Cambios" title for a frame while the modal fades out. The
    // next confirm() call will overwrite the state anyway.
  }, []);

  // Si el boundary atajó un error, se desmonta: el próximo confirm() monta un
  // boundary nuevo en lugar de quedar pegado en `failed`.
  const handleBoundaryError = useCallback(() => {
    setIsModalOpen(false);
    setIsModalMounted(false);
  }, []);

  const value = useMemo<ConfirmationContextType>(
    () => ({
      confirm: (modalConfig: ModalConfig) => {
        setConfirmOptions({ ...defaultConfirmOptions, ...modalConfig });
        setIsModalMounted(true);
        setIsModalOpen(true);
      }
    }),
    []
  );

  return (
    <ConfirmationContext value={value}>
      {children}
      {isModalMounted && (
        <ConfirmationModalBoundary onError={handleBoundaryError}>
          <Suspense fallback={null}>
            <ConfirmationModal {...confirmOptions} isOpen={isModalOpen} setIsOpen={handleModalClose} />
          </Suspense>
        </ConfirmationModalBoundary>
      )}
    </ConfirmationContext>
  );
};

export const useConfirmation = (): ConfirmationContextType => useContext(ConfirmationContext);

type AskConfig = Omit<ModalConfig, 'onConfirm' | 'onClose' | 'container'>;

/**
 * Reemplazo de `window.confirm` con el modal de la app: devuelve una promesa que
 * resuelve `true` al confirmar y `false` al cancelar (botón, Escape o clic afuera).
 */
export const useConfirm = () => {
  const { confirm } = useConfirmation();
  return useCallback(
    (config: AskConfig) =>
      new Promise<boolean>((resolve) => {
        confirm({
          ...config,
          onConfirm: () => resolve(true),
          onClose: () => resolve(false)
        });
      }),
    [confirm]
  );
};
